/**
 * The pull composite, factored out of the `pull` command so `switch` can reuse
 * it after rebinding to a new version.
 *
 * getPullUrl (RPC) → GET the presigned URL → unpack the tarball over the project
 * (additive overwrite; excluded dirs untouched). The server's getPullUrl records
 * the session's base revision + a `pulled` event as a side effect.
 *
 * Requires `SessionClient` in the environment, so the caller controls which
 * session (token) the pull authenticates as — `pull` uses the active session;
 * `switch` provides a fresh layer for the just-minted session.
 *
 * @module
 */
import { Effect, FileSystem } from "effect"
import { FetchHttpClient } from "effect/unstable/http"
import { ChildProcessSpawner } from "effect/unstable/process"
import { step } from "./with-session"
import * as archive from "./archive"
import * as transfer from "./transfer"
import { SessionClient } from "../rpc"
import type { OperationFailed } from "../errors"

/** Download the session's version bundle and unpack it over `projectDir`. */
export const downloadAndUnpack = (input: {
  readonly projectDir: string
}): Effect.Effect<
  void,
  OperationFailed,
  SessionClient | FileSystem.FileSystem | ChildProcessSpawner.ChildProcessSpawner
> =>
  Effect.scoped(
    Effect.gen(function* () {
      const fs = yield* FileSystem.FileSystem
      const client = yield* SessionClient

      const presigned = yield* step("pull:url", client.getPullUrl(void 0))
      const tmp = yield* step(
        "pull:tmp",
        fs.makeTempFileScoped({ suffix: ".tar.gz" }),
      )
      yield* step(
        "pull:download",
        transfer
          .download({ url: presigned.url, toFile: tmp })
          .pipe(Effect.provide(FetchHttpClient.layer)),
      )
      yield* step(
        "pull:unpack",
        archive.unpack({ archive: tmp, projectDir: input.projectDir }),
      )
    }),
  )
